/**
 * Helpers for route-owned titles. Each route declares its title the native
 * TanStack way — `head: () => ({ meta: [{ title }] })` — which `<HeadContent>`
 * (rendered in the root) turns into the document `<title>`. Presence reads the
 * same resolved `match.meta` so there's one source of truth for "where am I",
 * shared by the browser tab and the avatar tooltips.
 *
 * The `-` prefix keeps this file out of the generated route tree.
 */

/** Minimal shape of an active route match needed to resolve a title. */
export interface TitleMatch {
  routeId: string;
  /** Resolved head meta (`Array<{ title?: string; … }>`), typed `unknown` by the router. */
  meta?: unknown;
}

/** Last path segment — used by file/app route titles. */
export function basename(path: string): string {
  const clean = path.replace(/\/+$/, "");
  const i = clean.lastIndexOf("/");
  return i === -1 ? clean : clean.slice(i + 1);
}

function titleOf(meta: unknown): string | undefined {
  if (!Array.isArray(meta)) return undefined;
  for (const entry of meta) {
    if (entry && typeof entry === "object" && "title" in entry) {
      const title = (entry as { title?: unknown }).title;
      if (typeof title === "string" && title) return title;
    }
  }
  return undefined;
}

/**
 * The deepest active route's title (the one `<HeadContent>` shows in the tab),
 * or `undefined` if no match declares one.
 */
export function titleFromMatches(
  matches: ReadonlyArray<TitleMatch>,
): string | undefined {
  for (let i = matches.length - 1; i >= 0; i--) {
    const title = titleOf(matches[i]?.meta);
    if (title) return title;
  }
  return undefined;
}
